import { useState } from 'react';
import { Trash2, Plus, Pencil, Check, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { DatePicker } from './ui/datepicker';
import { OneTimeTransaction } from '../types';
import { formatCurrency } from '../lib/utils';

interface OneTimeTransactionsProps {
  transactions: OneTimeTransaction[];
  onTransactionsChange: (transactions: OneTimeTransaction[]) => void;
}

function OneTimeTransactions({ transactions, onTransactionsChange }: OneTimeTransactionsProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState<Date>(new Date());

  const handleAdd = () => {
    if (!name || !amount) return;
    
    const newTransaction: OneTimeTransaction = {
      id: Date.now(),
      name,
      amount: Number(amount),
      date,
    };

    onTransactionsChange([...transactions, newTransaction]);
    setName('');
    setAmount('');
    setIsFormOpen(false);
  };

  const handleDelete = (id: number) => {
    onTransactionsChange(transactions.filter(t => t.id !== id));
  };

  const startEditing = (transaction: OneTimeTransaction) => {
    setEditingId(transaction.id);
    setName(transaction.name);
    setAmount(transaction.amount.toString());
    setDate(transaction.date);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setName('');
    setAmount('');
    setDate(new Date());
  };

  const saveEdit = () => {
    if (!editingId || !name || !amount) return;

    onTransactionsChange(transactions.map(t => 
      t.id === editingId 
        ? { ...t, name, amount: Number(amount), date }
        : t
    ));
    setEditingId(null);
    setName('');
    setAmount('');
    setDate(new Date());
  };

  return (
    <div className="space-y-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 font-medium">Name</th>
            <th className="text-left py-2 font-medium">Amount</th>
            <th className="text-left py-2 font-medium">Date</th>
            <th className="text-right py-2 font-medium w-[50px]">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsFormOpen(!isFormOpen)}
              >
                <Plus className={`h-4 w-4 transition-transform ${isFormOpen ? 'rotate-45' : ''}`} />
              </Button>
            </th>
          </tr>
        </thead>
        <tbody>
          {isFormOpen && (
            <tr className="border-b border-border/50">
              <td className="py-2 pr-4">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </td>
              <td className="py-2 pr-4">
                <div className="relative">
                  <span className="absolute left-3 top-2.5">$</span>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-6"
                    placeholder="0.00"
                  />
                </div>
              </td>
              <td className="py-2 pr-4">
                <DatePicker
                  date={date}
                  onChange={(date) => date && setDate(date)}
                />
              </td>
              <td className="py-2 text-right">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleAdd}
                >
                  <Check className="h-4 w-4" />
                </Button>
              </td>
            </tr>
          )}
          {transactions.map((transaction) => (
            <tr 
              key={transaction.id}
              className="border-b border-border/50 last:border-0"
            >
              {editingId === transaction.id ? (
                <>
                  <td className="py-2 pr-4">
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name"
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <div className="relative">
                      <span className="absolute left-3 top-2.5">$</span>
                      <Input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="pl-6"
                        placeholder="0.00"
                      />
                    </div>
                  </td>
                  <td className="py-2 pr-4">
                    <DatePicker
                      date={date}
                      onChange={(date) => date && setDate(date)}
                    />
                  </td>
                  <td className="py-2 text-right space-x-1 flex">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={saveEdit}
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={cancelEditing}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </td>
                </>
              ) : (
                <>
                  <td className="py-2 pr-4">
                    {transaction.name}
                  </td>
                  <td className="py-2 pr-4">
                    <span className={transaction.amount >= 0 ? "text-green-600" : "text-red-600"}>
                      {transaction.amount >= 0 ? '+' : '-'}{formatCurrency(transaction.amount)}
                    </span>
                  </td>
                  <td className="py-2 pr-4">
                    {transaction.date.toLocaleDateString()}
                  </td>
                  <td className="py-2 text-right space-x-1 flex">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => startEditing(transaction)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => handleDelete(transaction.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </>
              )}
            </tr>
          ))}
          {transactions.length === 0 && !isFormOpen && (
            <tr>
              <td colSpan={4} className="py-4 text-center text-sm text-muted-foreground">
                No one-time transactions yet
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default OneTimeTransactions; 